/**
 * Tool Output Schemas
 *
 * Explicit output schemas for all AI tools.
 * These provide complete type safety for tool results across web/backend.
 *
 * Design principles:
 * 1. Match actual service return types from assistant-functions.service.ts
 *    and system-agent-functions.service.ts
 * 2. Use descriptive field names for AI consumption
 * 3. Support both success and error cases
 */

import { z } from "zod";

// ============================================================================
// Common Output Components
// ============================================================================

/**
 * Simple success response for tools that just indicate completion.
 * Used by: modifyEvent, cancelEvent, requestPhoneNumber
 */
export const ToolSuccessOutputSchema = z.object({
	ok: z.literal(true).describe("Indicates successful operation"),
});

/**
 * Successful channel-native interactive send. `providerMessageId` is the
 * WhatsApp wamid / Telegram message id / Instagram mid that the inbound tap
 * references, allowing the review UI to fold the later selection back into
 * this exact tool part. Some providers can acknowledge without returning an id,
 * so the field remains optional and correlation degrades safely.
 *
 * `ok` carries its own description here (overriding the shared one) because this
 * is the moment the model decides whether to keep writing: the tool has already
 * put the body text in the customer's chat, and without being told so the model
 * re-states the same question as a second message.
 */
export const sendInteractiveButtonsOutputSchema =
	ToolSuccessOutputSchema.extend({
		ok: z
			.literal(true)
			.describe(
				"Delivered. The body text and its options are already in the customer chat. This send IS the reply — end the turn without writing another message about it."
			),
		providerMessageId: z.string().min(1).optional(),
	});

export const sendInteractiveListOutputSchema =
	sendInteractiveButtonsOutputSchema;

/**
 * Successful phone request. Shaped like `ToolSuccessOutputSchema` but declared
 * separately so `ok` can carry the same "already delivered" note as the
 * interactive sends: this tool writes the prompt text (plus the consent and
 * E.164-fallback lines) and sends it itself, so a follow-up asking for the
 * number again lands as a duplicate.
 */
export const requestPhoneNumberOutputSchema = z.object({
	ok: z
		.literal(true)
		.describe(
			"Delivered. The request, and the share button on channels that have one, is already in the customer chat. This send IS the reply — end the turn without asking for the number again in text."
		),
});

export const scheduleEventOutputSchema = z.object({
	scheduled: z.boolean().describe("Whether an appointment was booked"),
	eventId: z.string().optional().describe("Created event ID when successful"),
	user_name: z
		.string()
		.nullable()
		.optional()
		.describe(
			"Resolved name of the messaging contact, including their saved name when omitted from the input."
		),
	booked_for: z
		.string()
		.optional()
		.describe(
			"Beneficiary the appointment was recorded for when booked on behalf of someone else. Absent = booked for the messaging contact themselves (including when beneficiary_name was ignored because it referred to the contact)."
		),
	position: z
		.number()
		.int()
		.positive()
		.optional()
		.describe("Current waitlist position when an offer could not be claimed"),
	reason: z
		.string()
		.optional()
		.describe("Stable failure reason when not booked"),
	message: z
		.string()
		.optional()
		.describe(
			"Actionable explanation of the failure: what went wrong and what to do next. Accompanies `reason` whenever the booking did not go through — read it before retrying."
		),
});

/**
 * Batch operation summary for tools that process multiple items.
 * Used by: scheduleEvents, modifyEvents, cancelEvents, etc.
 */
export const BatchOperationSummarySchema = z.object({
	ok: z.literal(true).describe("Indicates operation completed"),
	succeeded: z.number().int().describe("Number of successful operations"),
	failed: z.number().int().describe("Number of failed operations"),
	message: z.string().optional().describe("Additional information"),
});

/**
 * Individual operation result for detailed batch responses.
 */
export const BatchOperationResultSchema = z.object({
	id: z.number().int().optional().describe("Event ID if applicable"),
	phone: z.string().optional().describe("Phone number if applicable"),
	success: z.boolean().describe("Whether this operation succeeded"),
	error: z.string().optional().describe("Error message if failed"),
	eventIds: z.array(z.number().int()).optional().describe("Created event IDs"),
});

// ============================================================================
// Shared Tool Outputs
// ============================================================================

/**
 * Vacation enrichment for getCurrentDatetime: the business is either currently
 * on vacation (`status: 'current'`) or has one starting within 30 days.
 */
export const DatetimeVacationInfoSchema = z.union([
	z.object({
		status: z.literal("current").describe("Business is currently on vacation"),
		message: z.string().describe("Vacation title/note"),
		end_date: z.string().describe("Vacation end date (YYYY-MM-DD)"),
	}),
	z.object({
		message: z.string().describe("Vacation title/note"),
		start_date: z
			.string()
			.describe("Upcoming vacation start date (YYYY-MM-DD)"),
		end_date: z.string().describe("Upcoming vacation end date (YYYY-MM-DD)"),
	}),
]);

/**
 * Output schema for getCurrentDatetime tool.
 *
 * Matches the snake_case shape the service actually returns: Gregorian + Hijri
 * date, the business-local time formatted per the org time format, the day
 * name, an optional Ramadan flag, and optional vacation enrichment.
 */
export const getCurrentDatetimeOutputSchema = z.object({
	gregorian_date: z
		.string()
		.describe(
			"Current date in YYYY-MM-DD format (Gregorian), business timezone"
		),
	business_local_time: z
		.string()
		.describe("Current local time, formatted per the org time format"),
	hijri_date: z
		.string()
		.describe("Current date in the Hijri (Umm al-Qura) calendar (YYYY-MM-DD)"),
	day_name: z.string().describe('Short day-of-week name (e.g., "Mon")'),
	is_ramadan: z
		.literal(true)
		.optional()
		.describe("Present and true only when the current date falls in Ramadan"),
	vacation_info: DatetimeVacationInfoSchema.optional().describe(
		"Current or upcoming business vacation, if any"
	),
});

// ============================================================================
// RAG Document Search Output
// ============================================================================

/**
 * A single search result from the document store.
 */
export const SearchResultSchema = z.object({
	documentId: z.number().int().describe("ID of the source document"),
	chunkId: z.number().int().optional().describe("Chunk ID for citation"),
	marker: z
		.string()
		.optional()
		.describe(
			"Returned citation marker; copy as ⟦S7⟧ after supported claims, never invent."
		),
	filename: z.string().describe("Original filename"),
	content: z.string().describe("Relevant text chunk"),
	contextPrefix: z
		.string()
		.optional()
		.describe("Contextual prefix for the chunk"),
	title: z
		.string()
		.optional()
		.describe("The chunk's section heading, e.g. 'Refund Policy'"),
	breadcrumb: z
		.string()
		.optional()
		.describe(
			"The chunk's section header path, e.g. 'Handbook > Billing > Refunds'. Use readSection to pull the whole section when needed."
		),
	pageNumber: z.number().int().optional().describe("Source page number"),
	sheetName: z.string().optional().describe("Source Excel sheet name"),
	rowRange: z.string().optional().describe("Row range for table chunks"),
	chunkType: z
		.string()
		.optional()
		.describe("Chunk type: TEXT, TABLE, or MIXED"),
	score: z.number().describe("Relevance score (RRF combined)"),
	similarity: z
		.number()
		.optional()
		.describe(
			"Raw cosine similarity of the chunk (0..1), distinct from the RRF-fused score"
		),
	rank: z
		.number()
		.int()
		.optional()
		.describe("Final 1-based position of this hit in the result list"),
	semanticRank: z
		.number()
		.int()
		.optional()
		.describe(
			"Position in the semantic leg before fusion; absent = not matched semantically"
		),
	ftsRank: z
		.number()
		.int()
		.optional()
		.describe(
			"Position in the keyword leg before fusion; absent = not matched on keywords"
		),
	rerankScore: z
		.number()
		.optional()
		.describe("Cross-encoder relevance 0..1 (present only when reranking ran)"),
	figureAssetId: z
		.number()
		.int()
		.optional()
		.describe(
			"WS11 — id of a figure/diagram this chunk depicts. Present only when the source document allows images in answers; you may reference the figure when it answers a visual question."
		),
});

/**
 * A document → section group for a search result set (WS1.2). The header fields
 * (filename, documentDate, documentSummary) are authoritative for recency
 * reasoning — prefer the newest documentDate when sources conflict.
 */
export const SearchDocGroupSchema = z.object({
	documentId: z.number().int().describe("ID of the source document"),
	filename: z.string().describe("Original filename"),
	documentSummary: z
		.string()
		.nullable()
		.describe("One-paragraph document summary, or null when not yet generated"),
	documentDate: z
		.string()
		.describe("Document date (ISO yyyy-mm-dd); authoritative for recency"),
	sections: z
		.array(
			z.object({
				breadcrumb: z
					.string()
					.nullable()
					.describe("Section header path, or null when untitled"),
				hits: z
					.array(SearchResultSchema)
					.describe("Hits within this section, in relevance order"),
			})
		)
		.describe("Sections of this document that matched"),
});

/**
 * A structured citation reference for a search result source.
 * Included alongside results so the LLM can produce inline citations.
 */
export const SourceCitationSchema = z.object({
	sourceId: z.string().describe('Unique citation ID, e.g. "doc-3-chunk-42"'),
	filename: z.string().describe("Original filename of the source document"),
	pageNumber: z.number().int().optional().describe("Page number if applicable"),
	sheetName: z.string().optional().describe("Sheet name for Excel/CSV sources"),
	rowRange: z
		.string()
		.optional()
		.describe('Row range for table chunks, e.g. "1-50"'),
	chunkType: z
		.string()
		.optional()
		.describe("Chunk type: TEXT, TABLE, or MIXED"),
	score: z.number().describe("Relevance score"),
});

/**
 * Output schema for searchDocs tool.
 */
export const searchDocsOutputSchema = z.object({
	results: z
		.array(SearchResultSchema)
		.describe("Matching document chunks sorted by relevance"),
	groups: z
		.array(SearchDocGroupSchema)
		.optional()
		.describe(
			"The same hits grouped by document → section, with recency-bearing document headers. Use documentDate to prefer current over stale sources."
		),
	sources: z
		.array(SourceCitationSchema)
		.describe(
			"Citation references for the returned results. Use these to cite sources in your response."
		),
	totalDocuments: z.number().int().describe("Total indexed documents"),
	fullDocumentText: z
		.string()
		.optional()
		.describe(
			"Full extracted text of the document. Only present when fullText=true was requested."
		),
	status: z
		.literal("no_results")
		.optional()
		.describe("Present only when results is empty."),
	reason: z
		.enum(["no_match", "no_documents_in_scope"])
		.optional()
		.describe(
			"Why the search returned nothing: no_match = the corpus was searched and matched nothing; no_documents_in_scope = this assistant has no documents to search, so no query can succeed."
		),
	guidance: z
		.string()
		.optional()
		.describe(
			"What to do next after an empty result. Follow it instead of re-issuing the same query or answering from memory."
		),
});

/**
 * Output schema for getDocumentSchemas tool.
 */
export const getDocumentSchemasOutputSchema = z.object({
	metadataCategories: z
		.object({
			category: z.array(z.string()),
			tags: z.array(z.string()),
		})
		.describe("Available metadata categories for filtering"),
	documents: z
		.array(
			z.object({
				documentId: z.number().int(),
				filename: z.string(),
				documentType: z.string(),
				summary: z.string(),
				category: z.string(),
				sizeChars: z
					.number()
					.int()
					.describe("Extracted-text length; full-doc-load cost signal."),
				pageCount: z.number().int().optional(),
				sheets: z.array(
					z.object({
						name: z.string(),
						columns: z.array(
							z.object({
								name: z.string(),
								type: z.string(),
								samples: z.array(z.unknown()),
							})
						),
						rowCount: z.number().int(),
					})
				),
			})
		)
		.describe("Documents with structured data schemas"),
	fewShotExamples: z
		.array(
			z.object({
				question: z.string(),
				sql: z.string(),
			})
		)
		.optional()
		.describe(
			"WS10.4 — proven past (question, SQL) pairs from this org, most similar to your `question` first. Adapt the closest example rather than writing SQL from scratch. Present only when `question` was supplied and similar past successes exist."
		),
});

/**
 * Output schema for queryStructuredData tool.
 */
export const queryStructuredDataOutputSchema = z.object({
	columns: z.array(z.string()).describe("Column names in the result set"),
	rows: z
		.array(z.record(z.string(), z.unknown()))
		.describe("Query result rows"),
	rowCount: z.number().int().describe("Number of rows returned"),
	marker: z
		.string()
		.optional()
		.describe(
			"Returned citation marker for this result; copy as ⟦S7⟧ after figures drawn from it, never invent."
		),
});

export type ToolQueryStructuredDataOutput = z.infer<
	typeof queryStructuredDataOutputSchema
>;

// ============================================================================
// Assistant Tool Outputs
// ============================================================================

/**
 * Output schema for scheduleEvent tool.
 */

export const joinWaitlistOutputSchema = z.object({
	joined: z.boolean(),
	entryId: z.string().optional(),
	position: z.number().int().positive().optional(),
	reason: z.string().optional(),
	message: z
		.string()
		.optional()
		.describe(
			"Actionable explanation of the failure: what went wrong and what to do next. Accompanies `reason` whenever the join did not go through — read it before retrying."
		),
});

/**
 * Output schema for modifyEvent tool.
 */

/**
 * Output schema for cancelEvent tool.
 */

/**
 * Event summary for getUserData output.
 */
export const EventSummarySchema = z.object({
	id: z.string().describe("Event ID"),
	eventDateTime: z
		.string()
		.describe("Event datetime in local timezone (YYYY-MM-DDTHH:mm:ss)"),
	type: z.string().describe('Event type ID (e.g., "CHECK_UP", "FOLLOW_UP")'),
	status: z.string().describe("Event status (active, cancelled, completed)"),
	for: z
		.string()
		.optional()
		.describe(
			"Phoneless beneficiary name; absent for the contact's own event."
		),
});

/**
 * Contact profile block for getUserData output.
 */
export const UserDataProfileSchema = z.object({
	name: z
		.string()
		.nullable()
		.describe("Authoritative contact name, or null when unknown"),
});

/**
 * A phoneless family member managed by the contact.
 */
export const BeneficiarySummarySchema = z.object({
	id: z.string().describe("Beneficiary contact ID"),
	name: z.string().describe("Beneficiary full name"),
});

/**
 * Output schema for getUserData tool.
 */
export const getUserDataOutputSchema = z.object({
	user: UserDataProfileSchema.describe("The contact’s saved profile"),
	beneficiaries: z
		.array(BeneficiarySummarySchema)
		.describe("Phoneless family members managed by this contact"),
	events: z
		.array(EventSummarySchema)
		.describe("The contact’s events (own and beneficiaries’)"),
});

/**
 * Output schema for getAvailableTimeSlots tool.
 */
export const getAvailableTimeSlotsOutputSchema = z.object({
	times: z
		.array(z.string())
		.describe("Available time slots in HH:mm format (24-hour)"),
	note: z
		.string()
		.optional()
		.describe("Booking-policy note; obey when offering or booking slots."),
});

/**
 * Date with available slots for search results.
 */
export const DateWithSlotsSchema = z.object({
	date: z.string().describe("Date in YYYY-MM-DD format"),
	times: z
		.array(z.string())
		.describe("Available time slots in HH:mm format (24-hour)"),
});

/**
 * Output schema for searchAvailableAppointments tool.
 */
export const searchAvailableAppointmentsOutputSchema = z.object({
	dates: z
		.array(DateWithSlotsSchema)
		.describe("Dates with available slots (empty dates filtered out)"),
	note: z
		.string()
		.optional()
		.describe(
			"Booking-policy note for omitted dates; obey when offering or booking."
		),
});

/**
 * Output schema for sendBusinessLocation tool.
 *
 * Echoes the exact pin the customer received so review UIs can render it on a
 * map without re-reading org config (which may change later). `location` is
 * optional because older persisted tool calls carried a bare `{ ok: true }`.
 */
export const sendBusinessLocationOutputSchema = ToolSuccessOutputSchema.extend({
	// Unlike the interactive sends, this tool takes no text input, so a short
	// accompanying line IS the model's only way to speak and must stay allowed.
	// The duplication to warn off is narrower: the pin already displays the name
	// and address, so repeating them in text is what reads as a double message.
	ok: z
		.literal(true)
		.describe(
			"Delivered. The pin already shows the business name and address, so do not repeat them in text — add at most one short line, or nothing."
		),
	location: z
		.object({
			latitude: z
				.number()
				.min(-90)
				.max(90)
				.describe("Latitude of the sent pin"),
			longitude: z
				.number()
				.min(-180)
				.max(180)
				.describe("Longitude of the sent pin"),
			name: z.string().optional().describe("Business name shown with the pin"),
			address: z
				.string()
				.optional()
				.describe("Street address shown with the pin"),
		})
		.optional()
		.describe("The location that was sent to the customer"),
});

/**
 * Output schema for handoffToHuman tool.
 *
 * `alreadyDisabled` distinguishes a real handoff (false) from an idempotent no-op
 * (true) so the LLM can adjust its reply (no need to apologise twice if a human
 * was already taking over).
 */
export const handoffToHumanOutputSchema = z.object({
	ok: z.literal(true).describe("Indicates successful operation"),
	alreadyDisabled: z
		.boolean()
		.describe(
			"True when the conversation already had AI disabled (call was a no-op)."
		),
});

/**
 * Output schema for saveFeedback tool.
 *
 * Returns only `ok: true` because the LLM should not reason about the storage
 * outcome — feedback is fire-and-forget from the AI's perspective.
 */

/**
 * Output schema for collectLead tool. Fire-and-forget; the LLM does not
 * need to reason about the storage outcome.
 */

/**
 * Output schema for sendInteractiveButtons tool.
 */

/**
 * Output schema for sendInteractiveList tool.
 */

// ============================================================================
// System-Agent Tool Outputs (7 tools)
// ============================================================================

/**
 * Output schema for scheduleEvents tool (batch scheduling).
 */
export const scheduleEventsOutputSchema = z.union([
	// Summary response (default)
	BatchOperationSummarySchema,
	// Detailed response
	z.object({
		ok: z.literal(true),
		succeeded: z.number().int(),
		failed: z.number().int(),
		results: z
			.array(BatchOperationResultSchema)
			.optional()
			.describe("Detailed results per request"),
	}),
]);

/**
 * Output schema for modifyEvents tool (batch modification by ID).
 */
export const modifyEventsOutputSchema = z.union([
	BatchOperationSummarySchema,
	z.object({
		ok: z.literal(true),
		succeeded: z.number().int(),
		failed: z.number().int(),
		results: z.array(BatchOperationResultSchema).optional(),
	}),
]);

/**
 * Output schema for modifyEventRange tool (bulk modification by date range).
 */
export const modifyEventRangeOutputSchema = z.union([
	BatchOperationSummarySchema,
	z.object({
		ok: z.literal(true),
		succeeded: z.number().int(),
		failed: z.number().int(),
		message: z.string().optional(),
	}),
]);

/**
 * Output schema for cancelEvents tool (batch cancellation by ID).
 */
export const cancelEventsOutputSchema = z.union([
	BatchOperationSummarySchema,
	z.object({
		ok: z.literal(true),
		succeeded: z.number().int(),
		failed: z.number().int(),
		results: z.array(BatchOperationResultSchema).optional(),
	}),
]);

/**
 * Output schema for cancelEventRange tool (bulk cancellation by date range).
 */
export const cancelEventRangeOutputSchema = z.union([
	BatchOperationSummarySchema,
	z.object({
		ok: z.literal(true),
		succeeded: z.number().int(),
		failed: z.number().int(),
		message: z.string().optional(),
	}),
]);

/**
 * Event snapshot for getEventSnapshots output.
 */
export const EventSnapshotSchema = z.object({
	id: z.number().int().describe("Event ID"),
	eventDateTime: z
		.string()
		.describe("Event datetime in local timezone (YYYY-MM-DDTHH:mm:ss)"),
	type: z.string().describe('Event type ID (e.g., "CHECK_UP", "FOLLOW_UP")'),
	status: z.string().describe("Event status"),
});

/**
 * User with events for getEventSnapshots output.
 */
export const UserWithEventsSchema = z.object({
	user_id: z.string().describe("User ID"),
	phone_e164: z.string().describe("Phone number in E.164 format"),
	user_name: z.string().describe("User name"),
	events: z.array(EventSnapshotSchema).describe("Events for this user"),
});

/**
 * Output schema for getEventSnapshots tool.
 */
export const getEventSnapshotsOutputSchema = z.object({
	results: z
		.array(UserWithEventsSchema)
		.describe("Users with their matching events"),
});

/**
 * Output schema for getAvailableSlotsBatch tool.
 */
export const getAvailableSlotsBatchOutputSchema = z.object({
	dates: z
		.array(DateWithSlotsSchema)
		.describe("Dates with available slots (empty dates filtered out)"),
	note: z
		.string()
		.optional()
		.describe(
			"Booking-policy note for omitted dates; obey when offering or booking."
		),
});

// ============================================================================
// summarizeConversation — structured summary
// ============================================================================

/**
 * One reservation/event row in a conversation summary table.
 */
export const SummaryReservationSchema = z.object({
	date: z.string().describe("Local date in YYYY-MM-DD"),
	time: z.string().optional().describe("Local time in HH:mm if applicable"),
	type: z.string().optional().describe("Reservation/event type label"),
	status: z
		.string()
		.optional()
		.describe("Status (confirmed, cancelled, no-show, etc.)"),
	notes: z.string().optional().describe("Anything notable for this row"),
});

/**
 * One feedback entry surfaced from the conversation.
 */
export const SummaryFeedbackEntrySchema = z.object({
	sentiment: z.enum(["positive", "negative", "neutral"]),
	summary: z.string().describe("Short paraphrase in operator language"),
	quote: z
		.string()
		.optional()
		.describe("Customer's verbatim words, if quotable"),
});

/**
 * Structured summary payload — also persisted in
 * conversation_summary.structuredSummary as jsonb.
 */
export const ConversationSummaryPayloadSchema = z.object({
	clientName: z.string().describe("Best-known display name for the contact"),
	phoneE164: z.string().optional().describe("Contact phone in E.164 format"),
	channel: z.string().describe("Channel scope of the summary (e.g. whatsapp)"),
	dateRange: z
		.object({
			from: z.string().describe("Earliest message date (ISO 8601)"),
			to: z.string().describe("Latest message date (ISO 8601)"),
		})
		.describe("Inclusive date range covered by the summary"),
	overview: z
		.string()
		.describe("1-2 short paragraphs in the operator language giving the gist"),
	reservations: z
		.array(SummaryReservationSchema)
		.describe("Reservations / events table"),
	feedback: z.array(SummaryFeedbackEntrySchema).describe("Feedback gathered"),
	notes: z.array(z.string()).describe("Free-form notable observations"),
	issues: z
		.array(z.string())
		.describe("Open issues, complaints, blockers, escalations"),
	metadata: z.object({
		messageCount: z.number().int().describe("Number of messages summarized"),
		generatedAt: z.string().describe("ISO 8601 timestamp of generation"),
	}),
});

/**
 * Output schema for the system-agent summarizeConversation tool.
 */
export const summarizeConversationOutputSchema = z.object({
	ok: z.literal(true),
	summary: ConversationSummaryPayloadSchema,
});

// ============================================================================
// System-agent backstage tool outputs
// ============================================================================

export const SystemAgentToolErrorOutputSchema = z.object({
	error: z.string(),
	requiredPermission: z.string().optional(),
});
const DateRangeOutputSchema = z.object({
	startDate: z.string(),
	endDate: z.string(),
});
const JsonObjectSchema = z.record(z.string(), z.unknown());

export const getAnalyticsOutputSchema = z.union([
	z.object({
		range: DateRangeOutputSchema,
		metrics: JsonObjectSchema,
		answer: z.string().optional(),
	}),
	SystemAgentToolErrorOutputSchema,
]);
export const getUsageBreakdownOutputSchema = z.union([
	z.object({
		range: DateRangeOutputSchema,
		dailyMessages: z.object({
			used: z.number().int().nonnegative(),
			remaining: z.number().int().nonnegative(),
			limit: z.number().int().positive(),
		}),
		aiUsage: JsonObjectSchema,
		credits: JsonObjectSchema,
	}),
	SystemAgentToolErrorOutputSchema,
]);
const KnowledgeGapOutputSchema = z.object({
	id: z.string(),
	question: z.string(),
	source: z.string(),
	status: z.enum(["OPEN", "DRAFTED", "RESOLVED", "DISMISSED"]),
	occurrenceCount: z.number().int().positive(),
	lastSeenAt: z.string(),
});
export const getKnowledgeGapsOutputSchema = z.union([
	z.object({ gaps: z.array(KnowledgeGapOutputSchema) }),
	SystemAgentToolErrorOutputSchema,
]);
export const renderChartOutputSchema = z.object({ rendered: z.literal(true) });
const KnowledgeSourceOutputSchema = z.object({
	id: z.number().int().positive(),
	kind: z.string(),
	title: z.string(),
	enabled: z.boolean(),
	status: z.string(),
	updatedAt: z.string(),
});
export const manageKnowledgeSourceOutputSchema = z.union([
	z.object({
		ok: z.literal(true),
		source: KnowledgeSourceOutputSchema.optional(),
	}),
	SystemAgentToolErrorOutputSchema,
]);
export const ingestUploadedFileOutputSchema = z.union([
	z.object({
		ok: z.literal(true),
		documentId: z.number().int().positive(),
		status: z.string(),
	}),
	SystemAgentToolErrorOutputSchema,
]);
export const triggerRetrainingOutputSchema = z.union([
	z.object({ ok: z.literal(true), queued: z.boolean() }),
	SystemAgentToolErrorOutputSchema,
]);
export const getAgentSettingsOutputSchema = z.union([
	z.object({ settings: JsonObjectSchema }),
	SystemAgentToolErrorOutputSchema,
]);
const ContactSummaryOutputSchema = z.object({
	id: z.string(),
	name: z.string().nullable(),
	nickname: z.string().nullable().optional(),
	phoneE164: z.string().nullable().optional(),
	isActive: z.boolean(),
});
export const getContactSnapshotOutputSchema = z.union([
	z.object({
		contact: ContactSummaryOutputSchema,
		beneficiaries: z.array(
			ContactSummaryOutputSchema.extend({
				eventCount: z.number().int().nonnegative(),
			})
		),
		parent: ContactSummaryOutputSchema.nullable().optional(),
		identityCluster: z.array(ContactSummaryOutputSchema),
		eventRollup: JsonObjectSchema,
		latestConversationSummary: JsonObjectSchema.nullable().optional(),
	}),
	SystemAgentToolErrorOutputSchema,
]);
export const manageContactOutputSchema = z.union([
	z.object({
		ok: z.literal(true),
		contactId: z.string(),
		operation: z.string(),
	}),
	SystemAgentToolErrorOutputSchema,
]);
export const manageBeneficiaryOutputSchema = z.union([
	z.object({
		ok: z.literal(true),
		beneficiaryId: z.string(),
		operation: z.string(),
	}),
	SystemAgentToolErrorOutputSchema,
]);
const ConversationRowOutputSchema = z.object({
	contactId: z.string(),
	phoneE164: z.string().nullable().optional(),
	contactName: z.string().nullable(),
	channel: z.string(),
	lastMessageAt: z.string(),
	status: z.string(),
});
export const queryConversationsOutputSchema = z.union([
	z.object({
		conversations: z.array(ConversationRowOutputSchema),
		cursor: z.string().optional(),
	}),
	SystemAgentToolErrorOutputSchema,
]);
export const searchConversationsOutputSchema = z.union([
	z.object({ results: z.array(JsonObjectSchema) }),
	SystemAgentToolErrorOutputSchema,
]);
export const getConversationMetricsOutputSchema = z.union([
	z.object({ range: DateRangeOutputSchema, metrics: JsonObjectSchema }),
	SystemAgentToolErrorOutputSchema,
]);
const AutomationOutputSchema = z.object({
	id: z.string(),
	name: z.string(),
	url: z.string(),
	events: z.array(z.string()),
	enabled: z.boolean(),
});
export const manageAutomationOutputSchema = z.union([
	z.object({
		ok: z.literal(true),
		automation: AutomationOutputSchema.optional(),
	}),
	SystemAgentToolErrorOutputSchema,
]);
export const startIntegrationConnectionOutputSchema = z.union([
	z.object({ url: z.string().url(), expiresAt: z.string().optional() }),
	SystemAgentToolErrorOutputSchema,
]);
export const getChannelStatusOutputSchema = z.union([
	z.object({ channels: z.array(JsonObjectSchema) }),
	SystemAgentToolErrorOutputSchema,
]);
export const generateInsightsReportOutputSchema = z.union([
	z.object({
		periodDays: z.number().int().positive(),
		summary: z.string(),
		highlights: z.array(z.string()),
		risks: z.array(z.string()),
		actions: z.array(z.string()),
		charts: z.array(JsonObjectSchema),
	}),
	SystemAgentToolErrorOutputSchema,
]);

// ============================================================================
// Type Exports
// Note: Using "Tool" prefix for types that conflict with events.contract.ts
// ============================================================================

export type ToolGetCurrentDatetimeOutput = z.infer<
	typeof getCurrentDatetimeOutputSchema
>;
export type ToolSuccessOutput = z.infer<typeof ToolSuccessOutputSchema>;
export type ToolSendBusinessLocationOutput = z.infer<
	typeof sendBusinessLocationOutputSchema
>;
export type ToolInteractiveSendOutput = z.infer<
	typeof sendInteractiveButtonsOutputSchema
>;
export type ToolScheduleEventOutput = z.infer<typeof scheduleEventOutputSchema>;
export type ToolJoinWaitlistOutput = z.infer<typeof joinWaitlistOutputSchema>;
export type ToolGetUserDataOutput = z.infer<typeof getUserDataOutputSchema>;
export type ToolGetAvailableTimeSlotsOutput = z.infer<
	typeof getAvailableTimeSlotsOutputSchema
>;
export type ToolSearchAvailableAppointmentsOutput = z.infer<
	typeof searchAvailableAppointmentsOutputSchema
>;
export type ToolScheduleEventsOutput = z.infer<
	typeof scheduleEventsOutputSchema
>;
export type ToolModifyEventsOutput = z.infer<typeof modifyEventsOutputSchema>;
export type ToolModifyEventRangeOutput = z.infer<
	typeof modifyEventRangeOutputSchema
>;
export type ToolCancelEventsOutput = z.infer<typeof cancelEventsOutputSchema>;
export type ToolCancelEventRangeOutput = z.infer<
	typeof cancelEventRangeOutputSchema
>;
export type ToolGetEventSnapshotsOutput = z.infer<
	typeof getEventSnapshotsOutputSchema
>;
export type ToolGetAvailableSlotsBatchOutput = z.infer<
	typeof getAvailableSlotsBatchOutputSchema
>;
export type ToolSearchDocsOutput = z.infer<typeof searchDocsOutputSchema>;
export type SourceCitation = z.infer<typeof SourceCitationSchema>;
export type ToolGetDocumentSchemasOutput = z.infer<
	typeof getDocumentSchemasOutputSchema
>;
export type ConversationSummaryPayload = z.infer<
	typeof ConversationSummaryPayloadSchema
>;
export type SummaryReservation = z.infer<typeof SummaryReservationSchema>;
export type SummaryFeedbackEntry = z.infer<typeof SummaryFeedbackEntrySchema>;
export type ToolSummarizeConversationOutput = z.infer<
	typeof summarizeConversationOutputSchema
>;

/**
 * All tool output types mapped by tool name.
 */
export interface ToolOutputSchemas {
	cancelEvent: ToolSuccessOutput;
	cancelEventRange: ToolCancelEventRangeOutput;
	cancelEvents: ToolCancelEventsOutput;
	generateInsightsReport: z.infer<typeof generateInsightsReportOutputSchema>;
	getAgentSettings: z.infer<typeof getAgentSettingsOutputSchema>;
	getAnalytics: z.infer<typeof getAnalyticsOutputSchema>;
	getAvailableSlotsBatch: ToolGetAvailableSlotsBatchOutput;
	getAvailableTimeSlots: ToolGetAvailableTimeSlotsOutput;
	getChannelStatus: z.infer<typeof getChannelStatusOutputSchema>;
	getContactSnapshot: z.infer<typeof getContactSnapshotOutputSchema>;
	getConversationMetrics: z.infer<typeof getConversationMetricsOutputSchema>;
	// Shared tools
	getCurrentDatetime: ToolGetCurrentDatetimeOutput;
	getDocumentSchemas: ToolGetDocumentSchemasOutput;
	getEventSnapshots: ToolGetEventSnapshotsOutput;
	getKnowledgeGaps: z.infer<typeof getKnowledgeGapsOutputSchema>;
	getUsageBreakdown: z.infer<typeof getUsageBreakdownOutputSchema>;
	getUserData: ToolGetUserDataOutput;
	ingestUploadedFile: z.infer<typeof ingestUploadedFileOutputSchema>;
	joinWaitlist: ToolJoinWaitlistOutput;
	manageAutomation: z.infer<typeof manageAutomationOutputSchema>;
	manageBeneficiary: z.infer<typeof manageBeneficiaryOutputSchema>;
	manageContact: z.infer<typeof manageContactOutputSchema>;
	manageKnowledgeSource: z.infer<typeof manageKnowledgeSourceOutputSchema>;
	modifyEvent: ToolSuccessOutput;
	modifyEventRange: ToolModifyEventRangeOutput;
	modifyEvents: ToolModifyEventsOutput;
	queryConversations: z.infer<typeof queryConversationsOutputSchema>;
	queryStructuredData: ToolQueryStructuredDataOutput;
	renderChart: z.infer<typeof renderChartOutputSchema>;
	requestPhoneNumber: ToolSuccessOutput;
	// Assistant tools
	scheduleEvent: ToolScheduleEventOutput;
	// System-agent tools
	scheduleEvents: ToolScheduleEventsOutput;
	searchAvailableAppointments: ToolSearchAvailableAppointmentsOutput;
	searchConversations: z.infer<typeof searchConversationsOutputSchema>;
	searchDocs: ToolSearchDocsOutput;
	sendBusinessLocation: ToolSendBusinessLocationOutput;
	sendInteractiveButtons: ToolInteractiveSendOutput;
	sendInteractiveList: ToolInteractiveSendOutput;
	startIntegrationConnection: z.infer<
		typeof startIntegrationConnectionOutputSchema
	>;
	summarizeConversation: ToolSummarizeConversationOutput;
	triggerRetraining: z.infer<typeof triggerRetrainingOutputSchema>;
}
